import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import { m } from 'framer-motion';
import { Parallax } from 'react-scroll-parallax';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import config from '../../config';
import axios from 'axios';
import { fadeIn } from '../../Functions/GlobalAnimations';

// Importe ces composants si tu les as déjà, sinon tu peux les recréer
// import { Header } from '../components/Header'; // ← à adapter ou supprimer

const CheckoutSubscription = ({ style }) => {
  const { t, i18n } = useTranslation();
  const { id: subscriptionId } = useParams();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [user, setUser] = useState(null);
  const [tax, setTax] = useState(null);
  const [taxStamp, setTaxStamp] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('virement');
  const [isPaymentMethodDisabled, setIsPaymentMethodDisabled] = useState(false);

  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const isRtl = i18n.language === 'ar';

  // ────────────────────────────────────────────────
  // Fetch data (inchangé)
  // ────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error(t('checkout_subscription.errors.login_required'));
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    try {
      const userRes = await axios.get(`${config.SITE_API_URL}/user-profile`, {
        headers: { Authorization: `Bearer ${token}`,
      'x-api-key': config.SITE_API_KEY },
      });
      setUser(userRes.data.customer);

      const subRes = await axios.get(
        `${config.SITE_API_URL}/getsubscription/${subscriptionId}`,
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );
      setSubscription(subRes.data);

      const taxRes = await axios.get(`${config.SITE_API_URL}/active_tax`, {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
      });
      setTax(taxRes.data[0]?.name || 0);

      const stampRes = await axios.get(`${config.SITE_API_URL}/active_taxstamp`, {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
      });
      setTaxStamp(stampRes.data[0]?.name || 0);
    } catch (error) {
      console.error('Erreur fetch:', error);
      toast.error(error.response?.data?.message || t('checkout_subscription.errors.fetch_error'));
      setTimeout(() => navigate('/subscription'), 2000);
    }
  }, [subscriptionId, navigate, t]);

  useEffect(() => {
    if (subscriptionId) fetchData();
    else {
      toast.error(t('checkout_subscription.errors.missing_subscription_id'));
      navigate('/subscription');
    }
  }, [subscriptionId, fetchData, navigate]);

  // ────────────────────────────────────────────────
  // Calculs prix (adapté à ton subscription)
  // ────────────────────────────────────────────────
  const subtotal = subscription?.price_ttc || 0;
  const taxAmount = (subtotal * (tax || 0)) / 100;
  const stampAmount = taxStamp || 0;
  const total = subtotal;

  // ────────────────────────────────────────────────
  // Méthodes de paiement (adaptées à ton système)
  // ────────────────────────────────────────────────
  const PAYMENT_METHODS = [
    {
      id: 'flouci',
      name: t('checkout.flouci_option') ,
      subtitle: t('checkout.flouci_subtitle'),
      img: '/flouci.png',
      disabled: true, // Désactive Flouci pour le moment
    },
    {
      id: 'virement',
      name: t('checkout.virement_option'),
      subtitle: t('checkout.virement_subtitle'),
      img: '/virement_bank.png',
    },
    // Ajoute Konnect si tu l'utilises encore
    // { id: 'konnect', name: 'Konnect', subtitle: '...', img: '/konnect.png' },
  ];

  // Trouver la méthode de paiement sélectionnée
  const selectedPaymentMethod = PAYMENT_METHODS.find(method => method.id === selectedPayment);

  // Vérifier si la méthode sélectionnée est désactivée
  useEffect(() => {
    if (selectedPaymentMethod?.disabled) {
      setIsPaymentMethodDisabled(true);
      toast.warning(selectedPaymentMethod.message || t('checkout_subscription.payment_method_unavailable'), {
        autoClose: 4000
      });
    } else {
      setIsPaymentMethodDisabled(false);
    }
  }, [selectedPaymentMethod, t]);

  // ────────────────────────────────────────────────
  // Soumission (inchangée)
  // ────────────────────────────────────────────────
  const handleCheckout = useCallback(async () => {
    if (!user || !subscription || tax === null || isSubmitting) return;

    // Vérification supplémentaire: méthode de paiement désactivée
    if (isPaymentMethodDisabled) {
      toast.error(t('checkout_subscription.payment_method_unavailable'));
      return;
    }

    // Vérification que la méthode sélectionnée n'est pas désactivée
    if (selectedPaymentMethod?.disabled) {
      toast.error(selectedPaymentMethod.message || t('checkout_subscription.payment_method_unavailable'));
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${config.SITE_API_URL}/checkout_subscription`,
        {
          userId: user._id,
          subscriptionId,
          paymentMethod: selectedPayment,
        },
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );

      const { data } = response;

      if (!data.success) {
        throw new Error(data.message || "Erreur serveur");
      }

      if (data.payment?.payUrl) {
        toast.info(t('checkout_subscription.redirecting'));
        setTimeout(() => {
          window.location.href = data.payment.payUrl;
        }, 1200);
        return;
      }

      // Cas virement
      if (selectedPayment === 'virement') {
        // data.subscription est l'objet de la nouvelle souscription créée
      const createdSubscription = data.subscription;

      if (!createdSubscription?._id) {
        throw new Error("ID de souscription non reçu dans la réponse");
      }

      const ref = createdSubscription.code || 
                  createdSubscription.code_subscription || 
                  createdSubscription._id;

      // On passe le VRAI subId (celui de SubscriptionByUserModel)
      navigate(
        `/payment-pending-subscription?subId=${createdSubscription._id}&ref=${encodeURIComponent(ref)}&amount=${total.toFixed(3)}`
      );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('checkout_subscription.errors.checkout_error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [user, subscription, selectedPayment, total, navigate, t, isSubmitting, isPaymentMethodDisabled, selectedPaymentMethod]);

  if (!user || !subscription || tax === null) {
    return (
      <div className="flex justify-center items-center h-screen" style={{ direction }}>
        <Loader2 className="animate-spin mr-3" size={28} />
        <p className="text-lg">{t('checkout_subscription.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ ...style, direction }}>
      {/* Parallax Header */}
      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
              <Parallax
                className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
                translateY={[-40, 40]}
                style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
              />
              <Container className="h-full relative">
                <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
                  <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
                    <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
                {t('checkout_subscription.complete_purchase')}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>

      <main className="max-w-7xl mx-auto px-4 min-sm:px-6 min-lg:px-8 py-12 min-md:py-16">
    {/*     <div className="mb-10 text-center">
          <h2 className="text-3xl min-md:text-4xl font-bold mb-3" style={{ color: '#003479' }}>
            {t('checkout_subscription.complete_purchase')}
          </h2>
          <p className="text-gray-600 text-lg">
            {t('checkout_subscription.secure_easy')}
          </p>
        </div> */}

        <div className="grid min-lg:grid-cols-3 gap-8 xl:gap-10">
          {/* ─── Résumé de l'ordre (droite sur desktop) ─── */}
          <div className="min-lg:col-span-1 order-2 min-lg:order-1">
            <div
              className="rounded-2xl p-7 min-md:p-8 shadow-xl sticky top-8"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                borderLeft: '5px solid #f29f2f',
              }}
            >
              <h3 className="text-xlg font-bold mb-7 flex items-center gap-3" style={{ color: '#003479' }}>
                <span className="text-xlg">📋</span>
                {t('checkout_subscription.your_order')}
              </h3>

              <div className="space-y-5 mb-8">
                <div className="flex justify-between items-center py-4">
                  <span className="text-gray-700 font-medium">
                    {subscription.title?.[i18n.language] || subscription.title?.fr} ({subscription.nbr_doc || 0}{' '}
                    {t('checkout_subscription.documents')})
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between pt-4 text-lg font-bold border-t border-gray-300" style={{ color: '#003479' }}>
                    <span>{t('checkout_subscription.total')}</span>
                    <span>{total.toFixed(3)} {t('DT')}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl text-center text-sm font-medium bg-green-50 text-green-800 border border-green-200">
                ✓ {t('checkout_subscription.secure_transaction')}
              </div>
            </div>
          </div>

          {/* ─── Formulaire + méthodes de paiement (gauche sur desktop) ─── */}
          <div className="min-lg:col-span-2 order-1 min-lg:order-2">
            <div className="bg-white rounded-2xl shadow-xl p-7 min-md:p-10">
              {/* Infos utilisateur (lecture seule) */}
              <h3 className="text-xlg font-bold mb-7 flex items-center gap-3" style={{ color: '#003479' }}>
                <span className="text-xlg">👤</span>
                {t('checkout.user_info')}
              </h3>

              <div className="grid grid-cols-1 min-md:grid-cols-2 gap-6 mb-10">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.first_name')}
                  </label>
                  <input
                    type="text"
                    value={user.first_name_parent || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.last_name')}
                  </label>
                  <input
                    type="text"
                    value={user.last_name_parent || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.phone')}
                  </label>
                  <input
                    type="text"
                    value={user.parent_phone || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout_subscription.adresse')}
                  </label>
                  <input
                    type="text"
                    value={user.adresse.replace("Governorate", "") || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
              </div>

              {/* Méthodes de paiement */}
              <h3 className="text-xlg font-bold mb-7 flex items-center gap-3" style={{ color: '#003479' }}>
                <span className="text-xlg">💳</span>
                {t('checkout.payment_method')}
              </h3>

              <div className="grid grid-cols-1 min-sm:grid-cols-2 gap-5 mb-10">
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => {
                      if (!method.disabled) {
                        setSelectedPayment(method.id);
                      }
                    }}
                    className={`relative p-6 border-2 rounded-2xl transition-all duration-300 hover:shadow-md hover:border-[#0879bd] group ${
                      selectedPayment === method.id
                        ? 'border-[#0879bd] bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white'
                    }
                    ${method.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-[#0879bd] group'}`}
                    //Si flouci fonction changer etat de paiement disabled : flase 
                    // et supprimer cet ligne dans classname ${method.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-[#0879bd] group'}`}
                    disabled={method.disabled}
                  >
                    {selectedPayment === method.id && !method.disabled && (
                      <div
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold shadow"
                        style={{ backgroundColor: '#f29f2f' }}
                      >
                        ✓
                      </div>
                    )}
                    
                    {method.disabled && (
                      <div
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold shadow bg-gray-400"
                      >
                        ✗
                      </div>
                    )}
                    <img
                      src={method.img}
                      alt={method.name}
                      className="h-20 mx-auto mb-4 object-contain"
                    />
                    <p className={`font-semibold text-center text-lg mb-1 ${method.disabled ? 'text-gray-500' : ''}`}>
                      {method.name}
                      {method.etat && (
                        <span className={`block text-sm mt-1 ${method.disabled ? 'text-red-500' : 'text-gray-500'}`}>
                          {method.etat}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-center text-gray-500">{method.subtitle}</p>
                    
                    {method.disabled && (
                      <div className="mt-3 p-2 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 font-medium">
                          {method.message || t('checkout_subscription.unavailable')}
                        </p>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Bouton final */}
              <button
                onClick={handleCheckout}
                disabled={isSubmitting}
                className={`w-full py-3 rounded-2xl font-bold text-lg text-white transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-98 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                style={{
                  backgroundColor: '#f29f2f',
                  boxShadow: '0 10px 30px rgba(242, 159, 47, 0.25)',
                }}
              >
                {isSubmitting
                  ? t('checkout_subscription.processing')
                  : `${t('checkout_subscription.confirm')} ${total.toFixed(3)} ${t('DT')}`}
              </button>

            </div>
          </div>
        </div>
      </main>

      <ToastContainer position={isRtl ? 'bottom-right' : 'bottom-left'} autoClose={3000} />
    </div>
  );
};

export default CheckoutSubscription;